import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/Help";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Data
import { useApi } from "../../context/api.js";
import { useMaterialUIController } from "../../context";
import { styled } from "@mui/material";
import {
  setSelectedHomework,
  useHomework,
  createList,
  createHomework,
} from "../../context/homework";
import HomeworkMilestone from "../../components/HomeworkMilestone";
import HomeworkCategoryLabel from "../../components/HomeworkCategoryLabel";
import { formatDate } from "../../utils/date.js";
import { useHomeworkTodo } from "../../context/homework/homework-selectors.js";

function Homework({ title = "Devoirs à faire", additionalActions, userId, showAdd }) {
  const [_, dispatch] = useHomework();
  const homework = useHomeworkTodo(userId);

  const handleAdd = () => {
    if (!homework || !homework.length) {
      createList(dispatch, userId);
    } else {
      createHomework(dispatch, homework[0].id, userId);
    }
  };

  return (
    <>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDBox>
          <MDTypography variant="h6" gutterBottom>
            {title}
          </MDTypography>
          {homework && homework[0] && (
            <MDBox display="flex" alignItems="center" lineHeight={0}>
              <Icon
                sx={{
                  fontWeight: "bold",
                  color: ({ palette: { info } }) => info.main,
                  mt: -0.5,
                }}
              >
                done
              </Icon>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;<strong>{homework[0].percent.toFixed(0)}% fait</strong> jusqu&apos;au{" "}
                {formatDate(homework[0].end)}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
        <MDBox color="text" px={2}>
          {showAdd && (
            <Icon
              key="add"
              sx={{ cursor: "pointer", fontWeight: "bold" }}
              fontSize="medium"
              onClick={handleAdd}
            >
              add_circle_outline
            </Icon>
          )}
          {additionalActions}
        </MDBox>
      </MDBox>
      <MDBox>
        {!homework && (
          <div style={{ textAlign: "center" }}>
            <CircularProgress color="info" />
          </div>
        )}
        {homework && !homework.length && (
          <MDBox px={3} pb={2}>
            <MDTypography variant="button" fontWeight="regular" fontStyle="italic" color="text">
              Pas de devoirs cette semaine.
            </MDTypography>
          </MDBox>
        )}
        {homework && !!homework.length && (
          <MDBox px={3} pb={2}>
            {homework[0].types.map((h) => (
              <HomeworkType key={h.id} userId={userId} {...h} />
            ))}
          </MDBox>
        )}
      </MDBox>
    </>
  );
}
Homework.propTypes = {
  title: PropTypes.node,
  additionalActions: PropTypes.node,
  userId: PropTypes.string,
  showAdd: PropTypes.bool,
};

const HomeworkType = ({ userId, id, name, optional, categories, done, total }) => {
  const [open, setOpen] = useState(!optional);

  return (
    <MDBox>
      <MDBox display="flex" sx={{ cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <MDTypography variant={"h6"} sx={{ flex: 1 }}>
          {name} {total > 0 && "(" + done + "/" + total + ")"}
        </MDTypography>
        <ExpandMoreIcon
          sx={{
            transform: !open ? "rotate(180deg)" : "",
            transition: "transform 300ms ease-in-out",
          }}
        />
      </MDBox>
      {open && categories.map((c) => <HomeworkCategory key={c.id} userId={userId} {...c} />)}
    </MDBox>
  );
};
HomeworkType.propTypes = {
  userId: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  optional: PropTypes.bool,
  categories: PropTypes.array,
  done: PropTypes.number,
  total: PropTypes.number,
};

const HomeworkCategory = (props) => {
  const { userId, id, name, color, works, done, total } = props;

  return (
    <MDBox my={1} sx={{ padding: "0 0 0 8px" }}>
      {/*<MDTypography sx={{ color: color || "black" }} variant="subtitle2">*/}
      {/*{name}*/}
      {/*</MDTypography>*/}
      <HomeworkCategoryLabel name={name} color={color} sx={{ marginBottom: "4px" }} />
      <MDBox sx={{ paddingLeft: "8px" }}>
        {works.map((w) => (
          <HomeworkWork key={w.id} userId={userId} {...w} />
        ))}
      </MDBox>
    </MDBox>
  );
};
HomeworkCategory.propTypes = {
  userId: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  color: PropTypes.string,
  works: PropTypes.array,
  done: PropTypes.number,
  total: PropTypes.number,
};

const HomeworkWork = (props) => {
  const { userId, id, name, description, milestones, validation } = props;
  const [homework, dispatch] = useHomework();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <MDBox
      sx={{
        display: "flex",
        position: "relative",
        padding: "2px 8px 2px 8px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
      }}
      onClick={() => setSelectedHomework(dispatch, id, userId)}
    >
      {description && (
        <CustomWidthTooltip title={description}>
          <HelpIcon color="info" sx={{ position: "absolute", left: -22, top: 2 }} />
        </CustomWidthTooltip>
      )}
      <MDTypography
        sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        variant="button"
      >
        {name}
      </MDTypography>
      {validation !== "none" &&
        (milestones || []).map((m) => <HomeworkMilestone key={m.id} {...m} />)}
    </MDBox>
  );
};
HomeworkWork.propTypes = {
  userId: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  validation: PropTypes.string,
  milestones: PropTypes.array,
};

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    textAlign: "left",
  },
});

export default Homework;
